import { MRT_ColumnDef, MRT_FilterFn, MRT_RowData } from "material-react-table";
import { Chip, Box, TextField, Autocomplete, useTheme, type ChipProps } from "@mui/material";
import { TagValue } from "../types/tag";
import { TagsFilter } from "src/components/Grid/filters/TagsFilter";
import { TagType } from "src/components/Filter/TagFilter/TagFiltersToolbar";
import { resolveLegacyTagColors, resolveTagColors } from "src/components/TagManagement/tagColors";

export type TagColumnAppearance = "legacy" | "product-v2";

type TagColumnChipProps = ChipProps & {
    appearance: TagColumnAppearance;
    tagType: TagType;
    tagValue?: TagValue;
};

const TagColumnChip = ({ appearance, label, tagType, tagValue, sx, ...chipProps }: TagColumnChipProps) => {
    const theme = useTheme();
    const colors =
        appearance === "product-v2" ?
            resolveTagColors(theme, tagType, tagValue)
        :   resolveLegacyTagColors(tagType, tagValue);

    return (
        <Chip
            {...chipProps}
            label={label}
            size="small"
            sx={[
                {
                    backgroundColor: colors.background,
                    color: colors.foreground,
                    fontWeight: "bold",
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        />
    );
};

export interface TagColumnOptions {
    appearance?: TagColumnAppearance;
    tagTypes: TagType[];
    tagValues: TagValue[];
    onTagChange?: (entityId: string, tagTypeSlug: string, values: string[]) => void;
    readonly?: boolean;
}

/**
 * Custom filter function for tag columns (does nothing client-side, filtering is done server-side)
 */
const tagColumnFilterFn: MRT_FilterFn<any> = (row, id, filterValue) => {
    // Always return true since filtering is done server-side
    return true;
};

/**
 * Generate dynamic columns for each tag type
 */
export function generateTagColumns<Data extends MRT_RowData>(options: TagColumnOptions): MRT_ColumnDef<Data, any>[] {
    const { appearance = "legacy", tagTypes, tagValues, onTagChange, readonly = true } = options;

    const slugToDisplayName = tagValues.reduce(
        (acc, value) => {
            acc[value.slug] = value.name;
            return acc;
        },
        {} as Record<string, string>,
    );

    return tagTypes.map((tagType) => ({
        accessorFn: (row: Data) => getTagValuesForType(row.tags, tagType.slug),
        id: `tag_${tagType.slug}`,
        header: tagType.name,
        size: 200,
        enableSorting: true, // Server-side sorting: single-value tags sort by value, multi-value tags sort by first value
        enableColumnFilter: true,
        enableGlobalFilter: false,
        enableHiding: true, // Important: allows showing/hiding via column management
        // filterFn: tagColumnFilterFn,
        Filter: (mrtProps) => {
            const availableValues =
                tagValues.flatMap((tv) => {
                    if (tagType.id !== tv.tagType?.id) return [];

                    return {
                        id: tv.id,
                        // Use display name for URL storage (consistent with existing behavior)
                        // useApiFiltersUrlSync will convert displayName to slug for API requests
                        name: tv.name,
                    };
                }) ?? [];

            return (
                <TagsFilter
                    appearance={appearance}
                    mrtProps={mrtProps}
                    selectOptions={{
                        kind: "checkbox",
                        values: availableValues,
                        getId: (item) => item.id,
                        getName: (item) => item.name, // Returns display name for URL/filter value
                    }}
                />
            );
        },
        Cell: ({ row, cell }) => {
            const entity = row.original;
            const tagValuesForType = getTagValuesForType(entity.tags, tagType.slug);
            if (readonly || tagValuesForType.length === 0) {
                return (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {tagValuesForType.map((value, index) => {
                            const valueToDisplay = slugToDisplayName[value];
                            const tagValue = tagValues.find((i) => i.slug === value);
                            return (
                                <TagColumnChip
                                    appearance={appearance}
                                    key={`${value}-${index}`}
                                    label={valueToDisplay}
                                    tagType={tagType}
                                    tagValue={tagValue}
                                />
                            );
                        })}
                    </Box>
                );
            }

            // Editable version
            const availableValues = tagValues.filter((tv) => tv.tagType?.id === tagType.id).map((tv) => tv.name);

            return (
                <Autocomplete
                    multiple
                    size="small"
                    options={availableValues}
                    value={tagValuesForType}
                    freeSolo={tagType.allowFreeValues || false}
                    onChange={(_, newValues) => {
                        if (onTagChange) {
                            onTagChange(String(entity.id), tagType.slug, newValues);
                        }
                    }}
                    renderTags={(tagValues, getTagProps) =>
                        tagValues.map((option, index) => (
                            <TagColumnChip
                                {...getTagProps({ index })}
                                appearance={appearance}
                                key={index}
                                label={option}
                                tagType={tagType}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder={tagType.multiple ? "Add tags..." : "Select tag..."}
                        />
                    )}
                />
            );
        },
    }));
}

/**
 * Extract tag values for a specific tag type from entity tags
 */
function getTagValuesForType(
    entityTags: Record<string, string[]> | Array<{ type: string; value: string }> | undefined,
    tagTypeSlug: string,
): string[] {
    if (!entityTags) return [];

    // Handle both formats:
    // 1. New format: [{"type":"Client","value":"Carrefour",...}]
    // 2. Legacy format: {"client":["carrefour"]}

    if (Array.isArray(entityTags)) {
        // New format: array of tag objects
        return entityTags
            .filter((tag) => tag.type?.toLowerCase() === tagTypeSlug.toLowerCase())
            .map((tag) => tag.value)
            .filter(Boolean);
    } else if (typeof entityTags === "object") {
        // Legacy format: object with tag type slugs as keys
        return entityTags[tagTypeSlug] || [];
    }

    return [];
}

/**
 * Get default column visibility for tag columns (visible by default)
 */
export function getDefaultTagColumnVisibility(tagTypes: TagType[]): Record<string, boolean> {
    const visibility: Record<string, boolean> = {};

    tagTypes.forEach((tagType) => {
        visibility[`tag_${tagType.slug}`] = true; // Visible by default
    });

    return visibility;
}
